import { css, html, LitElement, type PropertyValues, type TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import type { RssNewsItem, NewsSlide, FhiSlide } from 'src/content/screens/slide-element';
import type { SelectDropdownOption } from 'src/library/editors/elements/select-dropdown-option';
import 'src/library/editors/elements/d-select-dropdown';
import { numberWord } from 'src/utilities/text';

export abstract class NewsSlideBase extends LitElement {
  static readonly styles = [
    css`
      :host {
        display: block;
        aspect-ratio: 16/9;
        container-type: inline-size;
      }
      .image-wrapper {
        position: relative;
        aspect-ratio: 16/9;
        overflow: hidden;
      }
      .front-image {
        position: absolute;
        bottom: 0;
        right: 0;
      }
      .logo {
        position: absolute;
        top: 0;
        right: 0;
      }
      .title {
        position: absolute;
        box-sizing: border-box;
        top: 0;
        left: 0;
        display: flex;
        align-items: flex-end;
        font-family: var(--screen-slide-header-font), sans-serif;
        font-weight: 600;
        font-size: 4.6cqi;
        line-height: 106%;
      }
      .description {
        position: absolute;
        box-sizing: border-box;
        bottom: 0;
        left: 0;
        height: 38.6cqw;
        font-family: var(--screen-slide-text-font), serif;
        font-weight: 400;
        font-size: 2.1cqi;
        line-height: 136%;
        hyphens: auto;
      }
      .controllers-position {
        position: absolute;
        bottom: 20px;
        right: 20px;
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
      }
      .controllers {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        gap: 12px;
        border-radius: 16px;
        box-shadow: 0 2px 10px hsla(1, 0%, 0%, 0.3);
        background: white;
        padding: 12px;
      }
      .overlay {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
      }
    `,
  ];
  @property({ type: Object })
  slide!: NewsSlide | FhiSlide;
  @property({ type: Array })
  feed: RssNewsItem[] = [];
  @property({ type: Object })
  item: RssNewsItem | undefined = undefined;
  @property({ type: Boolean })
  edit = false;
  protected interval: number | null = null;

  protected shortenContent(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    const segmenter = new Intl.Segmenter('no', { granularity: 'sentence' });
    const segments = segmenter.segment(text);
    let result = '';
    for (const { segment } of segments) {
      if ((result + segment).trim().length > maxLength) {
        break;
      }
      result += segment;
    }
    if (result.trim() === '') {
      return text.slice(0, maxLength).trim() + '...';
    }
    return result.trim();
  }

  protected get numberedItems(): RssNewsItem[] {
    const items: RssNewsItem[] = this.feed.slice(0, this.slide.itemsCount);
    return items.map((item, index) => {
      return { ...item, index };
    });
  }

  protected get countOptions(): SelectDropdownOption[] {
    const list = Array.from({ length: 20 }, (_, i) => i + 1);
    return list.map((l) => {
      const unit = l === 1 ? ' nyhet' : ' nyheter';
      return { value: l + '', text: numberWord(l) + unit };
    });
  }

  protected get durationOptions(): SelectDropdownOption[] {
    const list = Array.from({ length: 20 }, (_, i) => i + 1);
    return list.map((l) => {
      const unit = l === 1 ? ' sekund' : ' sekunder';
      return { value: l + '', text: numberWord(l, true) + unit };
    });
  }

  protected onDurationChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.slide = { ...this.slide, itemDuration: Number(e.detail.value) };
    this.stop();
    this.start();
    this.fireChange();
  }

  protected onCountChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.slide = { ...this.slide, itemsCount: Number(e.detail.value) };
    this.stop();
    this.start();
    this.fireChange();
  }

  protected step() {
    const items = this.numberedItems;
    if (!items.length) {
      this.item = undefined;
      return;
    }
    let newIndex = 0;
    if (this.item) {
      const index = this.item.index ?? 0;
      newIndex = (index + 1) % items.length;
    }
    this.item = items[newIndex];
  }

  protected start() {
    if (this.interval) return;
    this.interval = window.setInterval(() => {
      this.step();
    }, this.slide.itemDuration * 1000);
  }

  protected stop() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  protected fireChange() {
    const value: NewsSlide | FhiSlide = {
      ...this.slide,
      duration: this.slide.itemsCount * this.slide.itemDuration,
    };
    this.dispatchEvent(
      new CustomEvent<{ value: NewsSlide | FhiSlide }>('value-changed', {
        bubbles: true,
        composed: true,
        detail: { value },
      }),
    );
  }

  connectedCallback() {
    super.connectedCallback();
    this.start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stop();
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.item = this.numberedItems[0];
  }

  render(): TemplateResult<1> {
    return html``;
  }
}
